'use client';
/** Helpers for fetchAdminApi: header prep, error-response handling, AdminAPIError conversion. App-agnostic -- every dependency is a `@federa` package. */
import { isBrowser } from '@federa/utils/browser/detection';
import { isPlainObject } from '@federa/const/record-guards';
import { getErrorMessage } from '@federa/utils/errors/error-core';
import { isFetchNetworkError } from '@federa/utils/http/transport-failure-classify';
import { formatDateTimeISO } from '@federa/utils/dates/iso-arithmetic-core';
import { ERROR_MESSAGES } from '@federa/const/error-messages';
import { CUSTOM_HEADERS, STANDARD_HEADERS } from '@federa/const/http-header-names';
import { HTTP_STATUS } from '@federa/const/http-status';
import { CONTENT_TYPES } from '@federa/const/http-headers';
import { API_TOKEN_ROLE } from '@federa/const/auth-session';
import { LOGGER_TAGS } from '@federa/const/logging';
import { HTTP_CLIENT_ERROR_CODES, isAbortError } from '@federa/const/http-error-codes';
import { publishedFirebaseBridge } from '@federa/const/shared-window-state';
import { AdminAPIError } from '@federa/const/admin-api-error';
import { scopedLogger } from '@federa/utils/deps/registry';
import { stampRequestTraceparent } from './traced-edge-fetch.ts';

const log = scopedLogger(LOGGER_TAGS.ADMIN_API);

/** Create a request cancelled error (module-local: the sole caller is `handleCaughtError` below). */
function createRequestCancelledError(): AdminAPIError {
	return new AdminAPIError('Request cancelled', {
		code: HTTP_CLIENT_ERROR_CODES.REQUEST_CANCELLED,
		status: 0,
	});
}

/** Error body type for API responses */
type ErrorBody = {
	error?: string;
	code?: string;
	details?: Record<string, unknown>;
};

/**
 * Header prep for `fetchAdminApi`: the ADMIN-client defaults over the shared originating-edge `traceparent` stamp.
 *
 * The trace header is composed from `stampRequestTraceparent` (`./traced-edge-fetch.ts`) rather than spelled here,
 * because the admin client is no longer its only producer -- the un-authed browser flow clients stamp the same header
 * through the same helper, and a second copy of the "mint unless the caller supplied one" decision is how the two
 * surfaces drift. It stays HEADER-ONLY on both: no client-side span is minted, nothing is exported from the browser,
 * and no timing is recorded -- the value exists so a worker-side run can JOIN to the page interaction that provoked it
 * (`inboundTraceEnv` lifts it at the entry).
 *
 * The two defaults BELOW are the admin client's own and deliberately do NOT move into the shared stamp: a JSON
 * content-type is wrong for a surface that posts anything else, and `CUSTOM_HEADERS.USER_ROLE` is an admin-API
 * convention no worker may read as authority (`SEC-*` bans server-side reads of `x-user-role`).
 */
export function prepareHeaders(existingHeaders?: HeadersInit): Headers {
	const headers = stampRequestTraceparent(existingHeaders);
	if (!headers.has(STANDARD_HEADERS.CONTENT_TYPE)) {
		headers.set(STANDARD_HEADERS.CONTENT_TYPE, CONTENT_TYPES.JSON);
	}
	headers.set(CUSTOM_HEADERS.USER_ROLE, headers.get(CUSTOM_HEADERS.USER_ROLE) ?? API_TOKEN_ROLE);
	return headers;
}

/**
 * Get the current Firebase user: the SHARED bridge first (`window.__federaFirebase`, published by the host's
 * AuthProvider), falling back to the app-local SDK instance only when the bridge is unpublished or holds no
 * user. Null when not in a browser, not authenticated, or on error -- callers send the request bare and the
 * worker answers 401. `authStateReady()` is awaited on both paths so a mount-time call does not race session
 * restoration and drop the bearer.
 */
export async function getFirebaseUser(): Promise<{
	uid: string;
	phoneNumber: string | null;
	getIdToken(forceRefresh?: boolean): Promise<string>;
} | null> {
	if (!isBrowser()) return null;
	try {
		// The SHELL's instance first. `AuthProvider` publishes the Auth object it signed the user in on as
		// `window.__federaFirebase`; `getFirebaseAuth` below builds its own from config. Where those resolve to two
		// different instances the local one has no `currentUser`, so `fetchAdminApi` attaches no bearer and EVERY gated
		// call goes out bare -- which reads as "Missing or invalid Authorization header" from every worker at once, not
		// as an auth failure. Same two-instances class the MFE clients already avoid via `readBridgeIdToken`.
		const bridge = publishedFirebaseBridge();
		if (bridge) {
			await bridge.auth.authStateReady();
			if (bridge.auth.currentUser) return bridge.auth.currentUser;
		}
		const [{ getFirebaseAuth }, { getEnvironmentConfig }] = await Promise.all([
			import('@federa/components/auth/utils/firebase-client'),
			import('@federa/components/lib/environment'),
		]);
		const auth = getFirebaseAuth(getEnvironmentConfig);
		// Wait for the SDK to restore the persisted session before reading currentUser: without this, mount-time calls race restoration and drop the bearer token.
		await auth.authStateReady();
		return auth.currentUser ?? null;
	} catch (error) {
		log.error('Failed to get Firebase user:', { error: getErrorMessage(error) });
		return null;
	}
}

/**
 * THE SIGNED-IN CALLER'S BEARER, or null when nobody is signed in -- the two lines every authed browser caller in
 * this tree had spelt for itself.
 *
 * RESOLVED PER CALL, never captured: a Firebase ID token expires in about an hour and `getIdToken()` refreshes it
 * transparently, so a hoisted value starts 401ing partway through a session.
 *
 * NULL RATHER THAN A THROW for a signed-out caller. The request then goes out with no `Authorization` header and the
 * worker's own gate answers 401, which keeps ONE authority for that decision instead of a drifting client-side copy.
 */
export async function getFirebaseIdToken(): Promise<string | null> {
	const user = await getFirebaseUser();
	return user ? await user.getIdToken() : null;
}

/** Track whether the dev auth warning has already been logged this session */
let devAuthWarningShown = false;

/** Add development-only user headers for testing without Firebase token verification */
export async function addDevUserHeaders(headers: Headers): Promise<void> {
	const user = await getFirebaseUser();
	if (!user) {
		if (!devAuthWarningShown) {
			log.warn('DEV No authenticated user found - requests may fail');
			devAuthWarningShown = true;
		}
		return;
	}
	headers.set(CUSTOM_HEADERS.USER_ID, user.uid);
	if (user.phoneNumber) {
		headers.set(CUSTOM_HEADERS.USER_PHONE, user.phoneNumber);
		log.debug('DEV Using Firebase user:', {
			uid: user.uid,
			phone: user.phoneNumber,
		});
	} else {
		log.warn('DEV Firebase user has no phone number - backend mapping may fail');
	}
}

/** Narrow an unverified error-response body to `ErrorBody`; every field is optional, so the guard exists to reject a non-object or wrong-typed field. */
function isErrorBody(value: unknown): value is ErrorBody {
	if (!isPlainObject(value)) return false;
	const { error, code, details } = value;
	if (error !== undefined && typeof error !== 'string') return false;
	if (code !== undefined && typeof code !== 'string') return false;
	return details === undefined || isPlainObject(details);
}

async function extractErrorBody(response: Response): Promise<ErrorBody> {
	const contentType = response.headers.get(STANDARD_HEADERS.CONTENT_TYPE) ?? '';

	try {
		const errorResponse = response.clone();
		if (contentType.includes(CONTENT_TYPES.JSON)) {
			const parsed: unknown = await errorResponse.json();
			return isErrorBody(parsed) ? parsed : {};
		}
		const textBody = await errorResponse.text();
		const trimmed = textBody.trim();
		return trimmed ? { error: trimmed } : {};
	} catch {
		// Expected: response body may not be parseable (empty, HTML error pages)
		return {};
	}
}

/** Handle non-OK response by extracting error details and throwing AdminAPIError */
export async function handleErrorResponse(response: Response, url: string): Promise<never> {
	const errorBody = await extractErrorBody(response);
	const isRetryable = response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR || response.status === HTTP_STATUS.TOO_MANY_REQUESTS;

	const errorDetails = {
		url,
		status: response.status,
		code: errorBody.code ?? HTTP_CLIENT_ERROR_CODES.API_ERROR,
		message: errorBody.error ?? (response.statusText || `HTTP ${response.status}`),
		timestamp: formatDateTimeISO(null),
		retryable: isRetryable,
	};

	log.error('API error:', errorDetails);

	throw new AdminAPIError(errorDetails.message, {
		code: errorBody.code ?? HTTP_CLIENT_ERROR_CODES.API_ERROR,
		status: response.status,
		details: errorBody.details,
		retryable: isRetryable,
	});
}

export function handleCaughtError(opts: { error: unknown; url: string; wasExternallyAborted: boolean; signal?: AbortSignal }): never {
	const { error, url, wasExternallyAborted, signal } = opts;
	if (isAbortError(error)) {
		if (wasExternallyAborted || signal?.aborted) {
			throw createRequestCancelledError();
		}
		log.error('Request timed out before completion:', {
			url,
		});
		throw new AdminAPIError('Request timeout', {
			code: HTTP_CLIENT_ERROR_CODES.TIMEOUT,
			status: HTTP_STATUS.GATEWAY_TIMEOUT,
		});
	}

	// THE PREDICATE IS THE SHARED ONE, NOT A RETYPED COPY. This branch and `classifyTransportFailure`
	// (`utils/http/transport-failure-classify.ts`, which the worker fetch client and the app tier's flow client both
	// dial) answer the SAME question -- is this throw a platform network failure -- and each had spelled it by hand, so
	// a change to one left the other classifying the identical throw as UNKNOWN_ERROR, which is not retryable where
	// NETWORK_ERROR is. The whole classifier is not adoptable here: this entry splits the abort arm into
	// cancelled-vs-timeout and answers every arm as an `AdminAPIError` with its own status.
	if (isFetchNetworkError(error)) {
		log.error('Network error:', {
			url,
			error: error.message,
		});
		throw new AdminAPIError(ERROR_MESSAGES.NETWORK_ERROR, {
			code: HTTP_CLIENT_ERROR_CODES.NETWORK_ERROR,
			status: 0,
			retryable: true,
		});
	}

	if (error instanceof AdminAPIError) {
		throw error;
	}

	log.error('Unknown error:', {
		url,
		error: getErrorMessage(error),
		timestamp: formatDateTimeISO(null),
	});
	throw new AdminAPIError(getErrorMessage(error), {
		code: HTTP_CLIENT_ERROR_CODES.UNKNOWN_ERROR,
		status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
	});
}
